import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {reinitFeefo} from '../../custom-api/hooks/use-feefo'
import {Global, css} from '@emotion/react'

const ReviewScoreIcon = ({
    id,
    parentSku,
    bvData = 'inline_rating',
    variant = {},
    onVisibilityChange,
}) => {
    const stylesGlobal = useMultiStyleConfig('ReviewScoreIcon', variant)
    const [isVisible, setIsVisible] = useState(false)
    const bvRef = useRef(null)
    const feefoRef = useRef(null)
    const normalizedParentSku = parentSku?.toUpperCase().replace(/[^A-Z0-9_: ]/g, '')

    useEffect(() => {
        reinitFeefo()
    }, [id, parentSku])

    useEffect(() => {
        const bvElement = bvRef.current
        const feefoElement = feefoRef.current

        if (!bvElement && !feefoElement) return

        const updateVisibility = () => {
            const bvHeight = bvElement?.getBoundingClientRect().height || 0
            const bvContainer = bvElement?.querySelector('.bv_main_container')
            const bvContainerHeight = bvContainer?.getBoundingClientRect().height || 0
            const bvHasContent = bvContainerHeight > 0
            const bvReviewCountText = bvContainer?.querySelector(
                '.bv_numReviews_component_container .bv_text',
            )?.textContent
            const bvHasReviews =
                bvData !== 'inline_rating' || Number(bvReviewCountText?.replace(/\D/g, '')) > 0
            const feefoHeight = feefoElement?.getBoundingClientRect().height || 0
            const newIsVisible = (bvHeight > 0 && bvHasContent && bvHasReviews) || feefoHeight > 0
            setIsVisible(newIsVisible)
            onVisibilityChange?.(newIsVisible)
        }

        const resizeObserver = new ResizeObserver(updateVisibility)
        const mutationObserver = new MutationObserver(updateVisibility)

        if (bvElement) {
            resizeObserver.observe(bvElement)
            mutationObserver.observe(bvElement, {
                childList: true,
                subtree: true,
                characterData: true,
            })
        }
        if (feefoElement) resizeObserver.observe(feefoElement)

        updateVisibility()

        return () => {
            resizeObserver.disconnect()
            mutationObserver.disconnect()
        }
    }, [id, parentSku, bvData, onVisibilityChange])

    return (
        <>
            <Global
                styles={css`
                    ${stylesGlobal.global}
                `}
            />
            <Box
                {...stylesGlobal.container}
                position={isVisible ? undefined : 'absolute'}
                visibility={isVisible ? undefined : 'hidden'}
                pointerEvents={isVisible ? undefined : 'none'}
                height={isVisible ? undefined : '0'}
                overflow={isVisible ? undefined : 'hidden'}
            >
                {(id || normalizedParentSku) && (
                    <>
                        {id && (
                            <div
                                ref={bvRef}
                                data-bv-show={bvData}
                                data-bv-product-id={id.replace(/\s+/g, '_').replace(/[:+/]/g, '_')}
                            ></div>
                        )}
                        {normalizedParentSku && (
                            <div
                                className="feefo-product-stars-widget"
                                ref={feefoRef}
                                data-parent-product-sku={normalizedParentSku}
                                style={{pointerEvents: 'none'}}
                            ></div>
                        )}
                    </>
                )}
            </Box>
        </>
    )
}

ReviewScoreIcon.propTypes = {
    id: PropTypes.string,
    parentSku: PropTypes.string,
    bvData: PropTypes.string,
    onVisibilityChange: PropTypes.func,
    variant: PropTypes.object,
}

export default ReviewScoreIcon
